import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Combobox, Field, type ComboboxGroup } from "./ui";
import { offsetLabel, timezoneGroups } from "../lib/timezones";

/**
 * Every timezone the runtime knows, searchable.
 *
 * A combobox rather than a select, because the list is the whole IANA database.
 * Four hundred options is a scroll, and a native select's type-ahead matches
 * only the first characters of an option — so "new york" finds nothing in
 * `America/New_York`, and neither does "york". Typing either finds it here.
 *
 * One component rather than the same options built on the create form and the
 * edit form, because the two have to agree about one thing that is easy to get
 * wrong separately: the zone already in use is always in the list. Any IANA
 * zone is accepted by the API, and a picker whose value matches no option
 * shows nothing — so an untouched edit form could save a different zone than it
 * was opened with.
 */
export function TimezonePicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("schedules");
  /*
   * Built once per value. Four hundred zones is four hundred `Intl`
   * constructions for the offsets, which is cheap enough once and wasteful on
   * every keystroke — and the query filters the built list rather than
   * rebuilding it, so typing costs nothing.
   */
  const groups = useMemo<ComboboxGroup[]>(
    () =>
      timezoneGroups(value).map((group) => ({
        label: group.label,
        options: group.options.map((zone) => ({
          value: zone.value,
          // The city carries the search; the offset is the thing being compared
          // between two candidates, so it sits apart as a hint rather than
          // inside the name.
          label: zone.label.replace(/\s*\((UTC[^)]*)\)/, ""),
          hint: offsetLabel(zone.value),
        })),
      })),
    [value],
  );

  return (
    <Field
      label={t("timezone.label")}
      htmlFor="schedule-timezone"
      hint={t("timezone.hint")}
    >
      <Combobox
        id="schedule-timezone"
        value={value}
        onChange={onChange}
        groups={groups}
        disabled={disabled}
        placeholder={t("timezone.search")}
        emptyLabel={t("timezone.noMatch")}
      />
    </Field>
  );
}
